import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RegisterSkillsModal } from '@/components/RegisterSkillsModal';
import { RegisterToast, type RegisterToastType } from '@/components/RegisterToast';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { useRegister, useRegisterMutations, type RegisterEventItem } from '@/hooks/useRegister';
import { api } from '@/lib/axios';
import { localeMeta } from '@/lib/i18n';
import type { RegisterSkillsSelection } from '@/lib/registerSkillsConfig';
import '@/styles/register.css';

function EventDescription({ event }: { event: RegisterEventItem }) {
  const { locale, t } = useLocale();
  const [translate, setTranslate] = useState(false);
  const translation = useQuery({
    queryKey: ['register-description', event.id, event.description, locale],
    enabled: translate && Boolean(event.description.trim()),
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data } = await api.post<{ success: boolean; data?: { translation: string } }>(
        '/api/v1/translate', { text: event.description, target_lang: locale },
      );
      if (!data.success || !data.data?.translation) throw new Error('Translation failed');
      return data.data.translation;
    },
  });
  return (
    <div className="event-register-description">
      <div className="event-register-section-heading">
        <h3>{t('reg_details')}</h3>
        {event.description.trim() ? (
          <button type="button" className="event-register-translate"
            onClick={() => setTranslate(!translate)} aria-pressed={translate}
            disabled={translation.isFetching}>
            {translation.isFetching ? t('reg_translating') : translate
              ? t('messages_show_original')
              : `${t('messages_translate')} · ${localeMeta[locale].name}`}
          </button>
        ) : null}
      </div>
      {translate && translation.isError ? <p role="alert">{t('reg_translation_error')}</p> : null}
      <p className="event-register-copy" dir="auto">
        {(translate && translation.data) || event.description || t('reg_details_pending')}
      </p>
    </div>
  );
}

export function RegisterPage() {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useRegister();
  const { toggle } = useRegisterMutations();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalEventId, setModalEventId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: RegisterToastType } | null>(null);
  const events = data?.events ?? [];
  const selected = events.find((event) => event.id === selectedId) ?? events[0];
  const busy = toggle.isPending;

  async function submit(eventId: number, action: 'register' | 'unregister', skills?: RegisterSkillsSelection) {
    if (busy) return;
    try {
      const result = await toggle.mutateAsync({ eventId, action, skills });
      setToast({
        message: result.success
          ? t(result.isRegistered ? 'reg_registered_success' : 'reg_unregistered_success')
          : result.error ?? t('reg_error_processing'),
        type: result.success ? 'success' : 'error',
      });
    } catch {
      setToast({ message: t('reg_error_connection'), type: 'error' });
    }
  }

  function register() {
    if (!selected || !selected.running || selected.isRegistered || busy) return;
    if (selected.isEqualized) setModalEventId(selected.id);
    else void submit(selected.id, 'register');
  }

  return (
    <div className="container-ingame">
      <div className="calendar-container">
        <SimpleMenu title={t('register')} />
        <div className="event-register">
          {isLoading ? <div className="event-register-empty" role="status">{t('loading_data')}</div>
            : isError ? <div className="event-register-empty" role="alert">
              <p>{t('reg_load_error')}</p>
              <button type="button" className="event-register-action" onClick={() => void refetch()}>{t('reg_retry')}</button>
            </div>
            : !selected ? <div className="event-register-empty">{t('reg_no_events')}</div>
            : <div className="event-register-layout">
              <section className="event-register-list-panel" aria-label={t('reg_events')}>
                <div className="event-register-list">
                  {events.map((event) => (
                    <button type="button" key={event.id}
                      className={`event-register-row${selected.id === event.id ? ' is-selected' : ''}`}
                      aria-pressed={selected.id === event.id} aria-controls="register-event-detail"
                      onClick={() => { setSelectedId(event.id); setToast(null); }}>
                      <span className="event-register-name">{event.name}</span>
                      <span className={`event-register-status${event.running ? ' is-open' : ''}`}>
                        {t(event.isRegistered ? 'reg_joined' : event.running ? 'reg_open' : 'reg_closed')}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
              <section id="register-event-detail" className="event-register-detail" aria-labelledby="register-event-name" aria-busy={busy}>
                <div className="event-register-detail-body" key={selected.id}>
                  <div className="event-register-eyebrow">
                    <span>{t(selected.running ? 'reg_open' : 'reg_closed')}</span>
                    <span>{t(selected.isEqualized ? 'reg_equalized' : 'reg_standard')}</span>
                  </div>
                  <h2 id="register-event-name">{selected.name}</h2>
                  <EventDescription key={`${selected.id}-${locale}`} event={selected} />
                </div>
                <footer className="event-register-footer">
                  <p role="status">{t(selected.isRegistered ? 'reg_joined_hint' : selected.running ? 'reg_open_hint' : 'reg_closed_hint')}</p>
                  <div className="event-register-actions">
                    <button type="button" className="event-register-action is-primary"
                      disabled={busy || !selected.running || selected.isRegistered} onClick={register}>
                      {busy && toggle.variables?.eventId === selected.id && toggle.variables.action === 'register'
                        ? t('reg_processing') : t(selected.isRegistered ? 'reg_joined' : 'reg_register')}
                    </button>
                    <button type="button" className="event-register-action"
                      disabled={busy || !selected.running || !selected.isRegistered}
                      onClick={() => void submit(selected.id, 'unregister')}>
                      {busy && toggle.variables?.eventId === selected.id && toggle.variables.action === 'unregister'
                        ? t('reg_processing') : t('reg_cancel_registration')}
                    </button>
                  </div>
                </footer>
              </section>
            </div>}
        </div>
      </div>
      <RegisterSkillsModal open={modalEventId !== null} race={data?.race ?? 'european'}
        onClose={() => setModalEventId(null)}
        onConfirm={(skills) => {
          if (modalEventId !== null) void submit(modalEventId, 'register', skills);
          setModalEventId(null);
        }} />
      <RegisterToast message={toast?.message ?? null} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}
